/**
 * PositiveReflectionFlow — replaces the tone-neutral debrief with the
 * positive-first Human Performance close (owner Q3 spec).
 *
 *   1. What worked today    (chips + free entry)
 *   2. What improved today  (chips + free entry)
 *   3. Next growth point    (one short line)
 *   4. Calm target for next lesson (one short line)
 *
 * After step 3, the instructor is offered "Find out why" — a one-tap cause
 * picker for the growth point they just typed (Q2 post-lesson half).
 *
 * Language rules:
 *  - "Growth point", never "needs work"
 *  - "Learning moment", never "mistake"
 *
 * Props:
 *   workLessonId — string
 *   onSaved      — () => void (called after final save so caller can advance)
 *   onCancel     — () => void
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Sparkles, ArrowRight, ArrowLeft, Check, Lightbulb, Heart, Target } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const WORKED_SUGGESTIONS = [
  "Mirror checks before lane changes",
  "Calm approach speed",
  "Clear lane choice",
  "Smooth gear changes",
  "Confident gap judgement",
  "Steady steering",
  "Spotted hazards early",
  "Clean reverse parking",
];

const IMPROVED_SUGGESTIONS = [
  "Confidence at roundabouts",
  "Reading road markings",
  "Lane discipline",
  "Smoother braking",
  "Better mirror awareness",
  "Stronger observation routine",
  "Less hesitation at junctions",
  "Calmer in traffic",
];

export default function PositiveReflectionFlow({ workLessonId, onSaved, onCancel }) {
  const [step, setStep] = useState(1);
  const [worked, setWorked] = useState([]);
  const [improved, setImproved] = useState([]);
  const [growth, setGrowth] = useState("");
  const [target, setTarget] = useState("");
  const [causes, setCauses] = useState([]);
  const [savedCauseIds, setSavedCauseIds] = useState([]);
  const [saving, setSaving] = useState(false);
  const [showFindOutWhy, setShowFindOutWhy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // pre-load any existing reflection so we can resume mid-flow
      try {
        const [reflRes, causeOpts] = await Promise.all([
          axios.get(`${API}/api/human-performance/reflection/${workLessonId}`, { withCredentials: true }),
          axios.get(`${API}/api/human-performance/options/causes`, { withCredentials: true }),
        ]);
        if (cancelled) return;
        const r = reflRes.data || {};
        setWorked(r.what_worked || []);
        setImproved(r.what_improved || []);
        setGrowth(r.next_growth_point || "");
        setTarget(r.calm_target_next_lesson || "");
        setCauses(causeOpts.data?.options || []);
      } catch {
        /* OK to start blank */
      }
    })();
    return () => { cancelled = true; };
  }, [workLessonId]);

  function toggleWorked(label) {
    setWorked((cur) => (cur.includes(label) ? cur.filter((x) => x !== label) : [...cur, label]));
  }
  function toggleImproved(label) {
    setImproved((cur) => (cur.includes(label) ? cur.filter((x) => x !== label) : [...cur, label]));
  }

  async function tagCause(c) {
    try {
      await axios.post(
        `${API}/api/human-performance/causes/tag`,
        {
          work_lesson_id: workLessonId,
          growth_tag: growth.slice(0, 240),
          cause_id: c.id,
        },
        { withCredentials: true }
      );
      setSavedCauseIds((s) => [...s, c.id]);
      toast.success(`Cause noted: ${c.label}`);
    } catch {
      toast.error("Couldn't save cause.");
    }
  }

  async function finalSave() {
    setSaving(true);
    try {
      await axios.post(
        `${API}/api/human-performance/reflection`,
        {
          work_lesson_id: workLessonId,
          what_worked: worked,
          what_improved: improved,
          next_growth_point: growth || null,
          calm_target_next_lesson: target || null,
        },
        { withCredentials: true }
      );
      toast.success("Reflection saved — strong close.");
      if (onSaved) onSaved();
    } catch {
      toast.error("Couldn't save reflection. Try again.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div data-testid="positive-reflection-flow" className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 overflow-hidden">
      <header className="px-4 py-3 border-b border-slate-100 dark:border-slate-800 flex items-center gap-3 bg-gradient-to-r from-emerald-50 to-blue-50 dark:from-emerald-900/15 dark:to-blue-900/15">
        <Sparkles className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
        <div className="flex-1 min-w-0">
          <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-emerald-700 dark:text-emerald-400">Positive close</p>
          <p className="text-sm font-extrabold text-slate-900 dark:text-white">Step {step} of 4</p>
        </div>
        {onCancel && (
          <button
            data-testid="reflection-cancel"
            onClick={onCancel}
            className="text-xs text-slate-500 hover:underline"
          >
            Skip for now
          </button>
        )}
      </header>

      <div className="p-4 space-y-4">
        {step === 1 && (
          <Step
            icon={Heart}
            title="What worked today?"
            tone="emerald"
            help="Pick what they did well — and add anything specific."
            chips={WORKED_SUGGESTIONS}
            picked={worked}
            onToggle={toggleWorked}
            free={worked.find((w) => !WORKED_SUGGESTIONS.includes(w)) || ""}
            onFreeChange={(v) => {
              setWorked((cur) => {
                const without = cur.filter((x) => WORKED_SUGGESTIONS.includes(x));
                return v.trim() ? [...without, v.trim()] : without;
              });
            }}
            testid="reflection-step-worked"
          />
        )}

        {step === 2 && (
          <Step
            icon={Lightbulb}
            title="What improved today?"
            tone="blue"
            help="Even tiny improvements count. Confidence is built from these."
            chips={IMPROVED_SUGGESTIONS}
            picked={improved}
            onToggle={toggleImproved}
            free={improved.find((w) => !IMPROVED_SUGGESTIONS.includes(w)) || ""}
            onFreeChange={(v) => {
              setImproved((cur) => {
                const without = cur.filter((x) => IMPROVED_SUGGESTIONS.includes(x));
                return v.trim() ? [...without, v.trim()] : without;
              });
            }}
            testid="reflection-step-improved"
          />
        )}

        {step === 3 && (
          <div data-testid="reflection-step-growth" className="space-y-3">
            <Header icon={Target} title="Next growth point" tone="amber" />
            <p className="text-xs text-slate-600 dark:text-slate-400">
              Not "needs work" — a growth point. One specific thing to build next.
            </p>
            <textarea
              data-testid="reflection-growth-input"
              value={growth}
              onChange={(e) => setGrowth(e.target.value)}
              rows={2}
              placeholder="e.g. Confidence at multi-lane roundabouts"
              className="w-full px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm text-slate-900 dark:text-white placeholder:text-slate-400"
            />
            {growth.trim() && (
              <div className="rounded-xl border border-amber-200 dark:border-amber-800/50 bg-amber-50 dark:bg-amber-900/15 p-3 space-y-2">
                <button
                  type="button"
                  data-testid="find-out-why-toggle"
                  onClick={() => setShowFindOutWhy((v) => !v)}
                  className="w-full text-left text-sm font-bold text-amber-800 dark:text-amber-300 inline-flex items-center gap-1.5"
                >
                  <Sparkles className="w-3.5 h-3.5" />
                  Find out why
                  <span className="ml-auto text-[10px] font-semibold uppercase">{showFindOutWhy ? "Hide" : "Tap"}</span>
                </button>
                {showFindOutWhy && (
                  <>
                    <p className="text-[11px] text-amber-800/80 dark:text-amber-200/80 mb-1">
                      Causes, not symptoms. Tap whichever apply.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                      {causes.map((c) => {
                        const saved = savedCauseIds.includes(c.id);
                        return (
                          <button
                            key={c.id}
                            type="button"
                            data-testid={`cause-${c.id}`}
                            disabled={saved}
                            onClick={() => tagCause(c)}
                            className={`text-left px-2.5 py-2 rounded-lg border text-xs font-semibold transition-colors ${
                              saved
                                ? "border-emerald-400 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-200"
                                : "border-amber-300 bg-white dark:bg-slate-900 text-amber-900 dark:text-amber-200 hover:bg-amber-100 dark:hover:bg-amber-900/30"
                            }`}
                          >
                            {saved ? <Check className="inline w-3 h-3 mr-1" /> : null}
                            {c.label}
                          </button>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        )}

        {step === 4 && (
          <div data-testid="reflection-step-target" className="space-y-3">
            <Header icon={Sparkles} title="Calm target for next lesson" tone="rose" />
            <p className="text-xs text-slate-600 dark:text-slate-400">
              One small, specific thing to focus on. Should feel achievable.
            </p>
            <textarea
              data-testid="reflection-target-input"
              value={target}
              onChange={(e) => setTarget(e.target.value)}
              rows={2}
              placeholder="e.g. Spot lane arrows 50m before the give-way line"
              className="w-full px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm text-slate-900 dark:text-white placeholder:text-slate-400"
            />
          </div>
        )}
      </div>

      <footer className="px-4 py-3 border-t border-slate-100 dark:border-slate-800 flex items-center gap-2">
        <button
          type="button"
          data-testid="reflection-back"
          disabled={step === 1}
          onClick={() => setStep((s) => Math.max(1, s - 1))}
          className="inline-flex items-center gap-1 px-3 py-2 rounded-lg text-xs font-semibold text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-30"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Back
        </button>
        <div className="flex-1" />
        {step < 4 ? (
          <button
            type="button"
            data-testid="reflection-next"
            onClick={() => setStep((s) => s + 1)}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-bold bg-emerald-600 hover:bg-emerald-700 text-white"
          >
            Next <ArrowRight className="w-3.5 h-3.5" />
          </button>
        ) : (
          <button
            type="button"
            data-testid="reflection-save"
            onClick={finalSave}
            disabled={saving}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-bold bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-60"
          >
            {saving ? "Saving…" : "Save reflection"}
            <Check className="w-3.5 h-3.5" />
          </button>
        )}
      </footer>
    </div>
  );
}

function Header({ icon: Icon, title, tone }) {
  const colour = tone === "amber" ? "text-amber-600 dark:text-amber-400"
              : tone === "rose"   ? "text-rose-600 dark:text-rose-400"
              : tone === "blue"   ? "text-blue-600 dark:text-blue-400"
              :                     "text-emerald-600 dark:text-emerald-400";
  return (
    <h4 className={`text-base font-extrabold inline-flex items-center gap-1.5 ${colour}`}>
      <Icon className="w-4 h-4" /> {title}
    </h4>
  );
}

function Step({ icon, title, tone, help, chips, picked, onToggle, free, onFreeChange, testid }) {
  return (
    <div data-testid={testid} className="space-y-3">
      <Header icon={icon} title={title} tone={tone} />
      <p className="text-xs text-slate-600 dark:text-slate-400">{help}</p>
      <div className="flex flex-wrap gap-1.5">
        {chips.map((c) => {
          const on = picked.includes(c);
          return (
            <button
              key={c}
              type="button"
              data-testid={`reflection-chip-${c.toLowerCase().replace(/\s+/g, "-").slice(0, 30)}`}
              onClick={() => onToggle(c)}
              className={`px-3 py-1.5 rounded-full text-xs font-bold transition-colors ${
                on
                  ? "bg-emerald-600 text-white"
                  : "bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-700"
              }`}
            >
              {c}
            </button>
          );
        })}
      </div>
      <input
        data-testid="reflection-free-input"
        type="text"
        value={free}
        onChange={(e) => onFreeChange(e.target.value)}
        placeholder="Add anything specific…"
        className="w-full px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm text-slate-900 dark:text-white placeholder:text-slate-400"
      />
    </div>
  );
}
